import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { api, authStore } from '../lib/api.js';
import PasswordInput from '../components/PasswordInput.jsx';

export default function Onboarding({ onSuccess }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const selectedPlanId = searchParams.get('plan') ?? '';
  const [form, setForm] = useState({
    tenantName: '',
    slug: '',
    adminName: '',
    email: '',
    password: '',
    planId: selectedPlanId,
  });
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setForm((current) => ({ ...current, planId: selectedPlanId }));
    if (!selectedPlanId) {
      setSelectedPlan(null);
      return;
    }
    api
      .listPublicPlans()
      .then((plans) => setSelectedPlan(plans.find((plan) => plan.id === selectedPlanId) ?? null))
      .catch(() => setSelectedPlan(null));
  }, [selectedPlanId]);

  function set(field) {
    return (e) => setForm({ ...form, [field]: e.target.value });
  }

  async function submit(e) {
    e.preventDefault();
    setError('');
    setBusy(true);
    try {
      const res = await api.register(form);
      authStore.set(res.accessToken, res.refreshToken);
      onSuccess(await api.me());
      navigate(`/assinatura-inicial${form.planId ? `?plan=${form.planId}` : ''}`);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  const rail = selectedPlan?.slug === 'free' ? 'bg-[#00CA72]' : selectedPlan?.slug === 'pro' ? 'bg-[#6161FF]' : 'bg-slate-900';

  return (
    <div className="relative min-h-screen bg-[#eef2f7]">
      <div
        aria-hidden="true"
        className="absolute inset-y-0 right-0 w-[55%] bg-[linear-gradient(180deg,#dcecff_0%,#c7e4ff_46%,#9ee4f0_100%)]"
      />
      <div className="relative mx-auto max-w-6xl px-6 py-10 lg:px-8">
        <div className="mb-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-gradient-to-br from-sky-500 via-cyan-400 to-teal-300 text-white shadow-[0_16px_32px_-18px_rgba(14,165,233,0.9)]">
              <span className="material-symbols-outlined text-[18px]">sell</span>
            </div>
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-[0.28em] text-sky-700">EtiquetandoApp</p>
              <p className="text-[11px] text-slate-500">Estúdio de geração e impressão</p>
            </div>
          </div>
          <span className="text-[11px] font-semibold text-slate-400">Criar conta</span>
        </div>

        <div className="mx-auto grid max-w-4xl gap-6 lg:grid-cols-[1.1fr_0.9fr]">
          <section className="rounded-2xl border border-slate-200 bg-white p-8 transition-all duration-300 ease-out hover:-translate-y-1 hover:border-slate-300">
            <h1 className="studio-uniform-title font-h2 text-h2 font-extrabold tracking-[-0.02em] text-[#181B34]">Criar conta do EtiquetandoApp</h1>
            <p className="mt-2 text-[11px] leading-6 text-slate-500">
              Cadastre seu espaço e comece a operar com geração e impressão de etiquetas.
            </p>
            <form onSubmit={submit} className="mt-6 space-y-4">
              <Field label="Nome da empresa" value={form.tenantName} onChange={set('tenantName')} placeholder="Minha Empresa" />
              <Field label="Slug do espaço" value={form.slug} onChange={set('slug')} placeholder="minha-empresa" />
              <Field label="Seu nome" value={form.adminName} onChange={set('adminName')} placeholder="Administrador" />
              <Field label="E-mail" type="email" value={form.email} onChange={set('email')} placeholder="voce@empresa.com" />
              <Field label="Senha" type="password" value={form.password} onChange={set('password')} placeholder="mínimo 8 caracteres" />
              {error && (
                <p className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-[11px] text-red-700">{error}</p>
              )}
              <button
                type="submit"
                disabled={busy}
                className="w-full min-h-12 rounded-xl bg-[#6161FF] px-4 py-3 text-[11px] font-bold text-white transition-colors hover:bg-[#4F4FEA] disabled:cursor-not-allowed disabled:opacity-50"
              >
                {busy ? 'Criando…' : 'Criar espaço'}
              </button>
            </form>
            <p className="mt-4 text-center text-[11px] text-slate-500">
              Já tem conta?{' '}
              <Link to="/login" className="font-semibold text-[#4F46E5] transition-colors hover:text-[#4338CA]">
                Entrar
              </Link>
            </p>
          </section>

          <aside className="relative overflow-hidden rounded-2xl border border-slate-200 bg-white">
            <div className={`absolute inset-x-0 top-0 h-1.5 ${rail}`} />
            <div className="p-7">
              <p className="text-[11px] font-bold text-slate-500">Plano selecionado</p>
              {selectedPlan ? (
                <>
                  <h2 className="studio-uniform-title mt-2 font-h2 text-h2 font-extrabold tracking-[-0.02em] text-[#181B34]">{selectedPlan.name}</h2>
                  <p className="mt-2 text-[11px] leading-6 text-slate-600">{selectedPlan.description}</p>

                  <div className="mt-6 flex items-baseline gap-2">
                    <span className="text-[1.9rem] font-extrabold tracking-[-0.03em] text-[#181B34]">
                      {selectedPlan.priceCents === 0 ? 'Grátis' : formatMoney(selectedPlan.priceCents, selectedPlan.currency)}
                    </span>
                    <span className="text-[11px] text-slate-500">
                      {selectedPlan.priceCents === 0
                        ? 'Sem mensalidade'
                        : `Cobrança ${selectedPlan.period === 'YEARLY' ? 'anual' : 'mensal'}`}
                    </span>
                  </div>

                  <div className="mt-6 divide-y divide-slate-100 overflow-hidden rounded-xl border border-slate-200 text-[11px]">
                    <SpecRow label="Usuários" value={selectedPlan.maxUsers >= 100 ? 'Escala alta' : `${selectedPlan.maxUsers}`} />
                    <SpecRow label="Modelos" value={selectedPlan.maxTemplates >= 1000 ? 'Escala alta' : `${selectedPlan.maxTemplates}`} />
                  </div>

                  <ul className="mt-5 space-y-2">
                    {(selectedPlan.features ?? []).map((feature) => (
                      <li key={feature} className="flex items-start gap-2 text-[11px] leading-6 text-slate-700">
                        <span className="material-symbols-outlined mt-0.5 text-[14px] text-[#4F46E5]">check_circle</span>
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <Link
                    to="/planos"
                    className="mt-6 inline-flex rounded-xl border border-[#CFD2E5] bg-white px-4 py-2.5 text-[11px] font-semibold text-[#181B34] transition-colors hover:bg-[#F0F3FF]"
                  >
                    Trocar plano
                  </Link>
                </>
              ) : (
                <>
                  <p className="mt-3 text-[11px] leading-6 text-slate-600">
                    Você pode seguir com o cadastro agora e escolher um plano antes de finalizar a contratação.
                  </p>
                  <Link
                    to="/planos"
                    className="mt-6 inline-flex min-h-11 items-center rounded-xl bg-[#181B34] px-4 py-2.5 text-[11px] font-bold text-white transition-colors hover:bg-slate-800"
                  >
                    Escolher plano
                  </Link>
                </>
              )}
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}

function Field({ label, type = 'text', ...props }) {
  const inputCls =
    'w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-[11px] text-slate-900 shadow-[inset_0_1px_0_rgba(255,255,255,0.7)] outline-none transition placeholder:text-slate-400 focus:border-[#6161FF] focus:ring-4 focus:ring-[#6161FF]/10';
  return (
    <label className="block">
      <span className="mb-2 block text-[11px] font-semibold text-[#181B34]">{label}</span>
      {type === 'password' ? (
        <PasswordInput {...props} className={`${inputCls} pr-10`} />
      ) : (
        <input type={type} {...props} className={inputCls} />
      )}
    </label>
  );
}

function SpecRow({ label, value }) {
  return (
    <div className="flex items-center justify-between px-4 py-3">
      <span className="text-slate-500">{label}</span>
      <span className="font-bold text-[#181B34]">{value}</span>
    </div>
  );
}

function formatMoney(cents, currency = 'BRL') {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency,
    minimumFractionDigits: cents % 100 === 0 ? 0 : 2,
  }).format(cents / 100);
}


